import * as React from "react";
import { DropzoneProps, useDropzone } from "react-dropzone";
import { fileUploadStore, InitModalOpts, UploadMode } from "../FileUpload/FileUploadStore";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import i18n, { I18N_NS } from "Config/i18n";
import { waitFor } from "utils/waitFor";
import { checkFileAccess } from "utils/checkFileAccess";
import { messageInstance } from "App";
import { useTranslation } from "react-i18next";

interface DocumentsDnDTestProps {
  children: React.ReactNode;
  disabled?: boolean;
  uploadMode: UploadMode;
}

export const DocumentsDnD = ({ children, disabled, uploadMode }: DocumentsDnDTestProps) => {
  const { t } = useTranslation(I18N_NS.COMPONENT_UPLOAD);
  const handleDrop: DropzoneProps["onDrop"] = React.useCallback(async (acceptedFiles: File[], _, event) => {
    event.preventDefault();
    event.stopPropagation();
    console.log("DocumentsDnD handleDrop", acceptedFiles);
    processDrop(acceptedFiles);
  }, []);

  const processDrop = React.useCallback(
    async (files: File[]) => {
      try {
        const initData: InitModalOpts = {};
        if (uploadMode === "VIEW") {
          initData.module = "VIEW";
        } else {
          initData.folderId = documentsTableStore.currentFolderId;
        }
        // Check file access for each file asynchronously
        const accessChecks = await Promise.all(files.map((file) => checkFileAccess(file)));
        if (accessChecks.some((result) => !result)) {
          messageInstance.error(
            t(
              // eslint-disable-next-line max-len
              "Cannot access file. It may be locked by another application, or you have problems with your network connection.",
              { I18N_NS: I18N_NS.COMPONENT_UPLOAD }
            )
          );
          // Remove inaccessible files from upload
          files = files.filter((_, index) => accessChecks[index]);
          if (files.length === 0) return undefined;
        }

        await fileUploadStore.initModal(initData);
        await waitFor(500);
        fileUploadStore.setFilesToUpload(files);
      } catch (error) {
        throw error;
      }
    },
    [uploadMode]
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    disabled,
    noClick: true,
    preventDropOnDocument: true,
    onDropRejected: (rejections) => {
      const errorMessages = rejections.map(
        (rejection) => `${rejection.file.name}: ${rejection.errors.map((e) => e.message).join(", ")}`
      );

      messageInstance.error(
        <>
          <div>{t("Some files could not be uploaded:")}</div>
          <ul>
            {errorMessages.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </>
      );
    },
    onError: (error) => {
      console.error("Dropzone error:", error);
      messageInstance.error(
        t("An error occurred while processing the files. They may be locked by another application.")
      );
    },
  });

  return (
    <div {...getRootProps({ className: isDragActive ? "documents-dnd dragover" : "documents-dnd" })}>
      <input {...getInputProps()} />
      <div className="documents-dnd-placeholder">
        <div>
          <FontAwesomeIcon icon={["fas", "file-upload"]} fixedWidth={true} size="lg" className="mb-2" />
          <div>{i18n.t("Drop here to start upload", { ns: I18N_NS.PROJECT }) as string}</div>
        </div>
      </div>
      {isDragActive ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            zIndex: 1,
            background: "url(data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7)",
          }}
        ></div>
      ) : null}
      {children}
    </div>
  );
};
